// DVAG-Kontakthistorie im Schriftverkehr — Spiegel von
// apps/api/src/dvag-post/dvag-post.service.ts.

import { apiUrl } from "../utils/api";

export type DvagOrdner = "posteingang" | "archiv";
/** Schäden stehen in einer eigenen Historie, alles andere in der Kontakthistorie. */
export type DvagBereich = "kontakt" | "schaden";

export const DVAG_BEREICH_TITEL: Record<DvagBereich, string> = {
    kontakt: "Kontakthistorie",
    schaden: "Schadenhistorie",
};

/** Filter der Liste; leere Werte filtern nicht. `team` ist eine Team-ID oder `ohne`. */
export interface DvagFilter {
    team?: string;
    kategorie?: string;
    produktgeber?: string;
    art?: string;
    prioritaet?: string;
    /** ISO-Datum `JJJJ-MM-TT`, einschließlich. */
    von?: string;
    bis?: string;
    status?: "gelesen" | "ungelesen";
    zuordnung?: "zugeordnet" | "vorschlag" | "offen";
    vorgang?: "mit" | "ohne";
    dokument?: "mit" | "ohne";
    kommentar?: boolean;
}

/** Auswahlwerte der Filter: nur, was in der sichtbaren Menge vorkommt. */
export interface DvagFilterWerte {
    kategorien: string[];
    produktgeber: string[];
    arten: string[];
    prioritaeten: string[];
    teams: Array<{ id: string; name: string; anzahl: number }>;
    ohneTeam: number;
}

export interface DvagZaehler {
    offen: number;
    ungelesen: number;
    schaden: { offen: number; ungelesen: number };
}

/** Anzahl der gesetzten Filter (für die Markierung am Filterknopf). */
export function dvagFilterAnzahl(f: DvagFilter): number {
    return Object.values(f).filter((v) => v !== undefined && v !== "" && v !== false).length;
}

export interface DvagKontaktKurz {
    id: number;
    name: string;
}

export interface DvagPostZeile {
    id: number;
    /** ISO-Datum `JJJJ-MM-TT`. */
    datum: string;
    betreff: string;
    name: string;
    nummer: string;
    produktgeber: string;
    art: string;
    kategorie: string;
    prioritaet: string;
    hatKommentar: boolean;
    hatPdf: boolean;
    pdfName: string | null;
    pdfBytes: number | null;
    gelesen: boolean;
    archiviertAm: string | null;
    kontakt: DvagKontaktKurz | null;
    vorschlag: (DvagKontaktKurz & { grund: string | null }) | null;
    contractId: number | null;
    vorgangId: number | null;
    documentId: number | null;
}

export interface DvagPostDetail extends DvagPostZeile {
    detail: {
        titel?: string;
        felder?: Record<string, string>;
        anhaenge?: Array<{ text: string; art: string }>;
        merkmale?: string[];
    };
}

export interface DvagPostListe {
    items: DvagPostZeile[];
    gesamt: number;
    ungelesen: number;
    /** Tatsächlich gelieferte Seite (1-basiert); liegt die gewünschte hinter dem Ende, die letzte. */
    seite: number;
    seitenGroesse: number;
}

async function ok<T>(res: Response, was: string): Promise<T> {
    if (!res.ok) throw new Error(`${was}: HTTP ${res.status}`);
    return (await res.json()) as T;
}

const post = (pfad: string, body: unknown): Promise<Response> =>
    fetch(apiUrl(pfad), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
    });

/** Menü und Ansicht hören darauf, damit der Zähler nach jeder Aktion stimmt. */
export const DVAG_POST_GEAENDERT = "kmvs:dvag-post-geaendert";
export const meldeDvagPostGeaendert = (): void => {
    window.dispatchEvent(new Event(DVAG_POST_GEAENDERT));
};

export const dvagPostService = {
    liste(
        ansicht: { bereich: DvagBereich; ordner: DvagOrdner; suche: string; seite: number; filter: DvagFilter },
        signal?: AbortSignal,
    ): Promise<DvagPostListe> {
        const { bereich, ordner, suche, seite, filter } = ansicht;
        const params: Record<string, string> = { bereich, ordner, seite: String(seite) };
        if (suche.trim()) params.q = suche.trim();
        for (const [k, v] of Object.entries(filter)) {
            if (v === true) params[k] = "1";
            else if (typeof v === "string" && v) params[k] = v;
        }
        return fetch(apiUrl("dvag-post", params), { signal }).then((r) => ok(r, DVAG_BEREICH_TITEL[bereich]));
    },
    filterWerte(bereich: DvagBereich, signal?: AbortSignal): Promise<DvagFilterWerte> {
        return fetch(apiUrl("dvag-post/filter", { bereich }), { signal }).then((r) => ok(r, "Filterwerte"));
    },
    zaehler(): Promise<DvagZaehler> {
        return fetch(apiUrl("dvag-post/zaehler")).then((r) => ok(r, "Kontakthistorie-Zähler"));
    },
    detail(id: number, signal?: AbortSignal): Promise<DvagPostDetail> {
        return fetch(apiUrl(`dvag-post/${id}`), { signal }).then((r) => ok(r, "Eintrag"));
    },
    /** Das PDF als Blob: der Abruf braucht den Bearer-Token, eine nackte URL trüge ihn nicht. */
    async pdf(id: number, signal?: AbortSignal): Promise<Blob> {
        const res = await fetch(apiUrl(`dvag-post/${id}/pdf`), { signal });
        if (!res.ok) throw new Error(`Dokument: HTTP ${res.status}`);
        return res.blob();
    },
    setzeGelesen(id: number, gelesen: boolean): Promise<DvagPostDetail> {
        return post(`dvag-post/${id}/gelesen`, { gelesen }).then((r) => ok(r, "Lesestatus"));
    },
    setzeArchiviert(id: number, archiviert: boolean): Promise<DvagPostDetail> {
        return post(`dvag-post/${id}/archiv`, { archiviert }).then((r) => ok(r, "Archivieren"));
    },
    ordneZu(id: number, contactId: number | null): Promise<DvagPostDetail> {
        return post(`dvag-post/${id}/kontakt`, { contactId }).then((r) => ok(r, "Zuordnung"));
    },
    /**
     * Bindet den Eintrag an einen Vorgang oder ein To-do darin: das PDF wird dort
     * als Dokument abgelegt, der Eintrag wandert ins Archiv.
     * T-397: Mit `dateiname` bekommt das Dokument am Vorgang einen neuen Namen.
     */
    verknuepfeVorgang(
        id: number,
        ziel: { vorgangId?: number; todoId?: number; dateiname?: string },
    ): Promise<DvagPostDetail & { dokumentAngehaengt?: boolean }> {
        return post(`dvag-post/${id}/vorgang`, ziel).then((r) => ok(r, "Vorgang"));
    },
    /**
     * T-298: Das PDF in einen Ordner der Kundenakte legen. Danach ist der Eintrag
     * aus der Kontakthistorie verschwunden. Die Meldung des Servers („Keine
     * Schreibrechte auf diesen Kontakt") geht unverändert an die Oberfläche.
     * T-396: Mit `dateiname` bekommt die Kopie beim Kunden einen neuen Namen.
     */
    async legeBeimKundenAb(
        id: number,
        ziel: { contactId: number; ordner: string; dateiname?: string },
    ): Promise<{ documentId: number; contactId: number; ordner: string }> {
        const res = await post(`dvag-post/${id}/ablage`, ziel);
        if (!res.ok) {
            let text = "";
            try {
                const body = (await res.json()) as { message?: unknown };
                text = Array.isArray(body?.message) ? body.message.join(", ") : String(body?.message ?? "");
            } catch {
                /* kein JSON */
            }
            if (res.status === 404) text = "Der Eintrag ist nicht mehr da — vermutlich wurde er schon abgelegt.";
            throw new Error(text || `Das Dokument konnte nicht abgelegt werden (HTTP ${res.status}).`);
        }
        return res.json();
    },
};

/**
 * Virtuelle Ordner im Mail-Menü: kein Graph-Ordner, sondern die DVAG-Ansicht.
 * Kontakthistorie `dvag:posteingang`, Schadenhistorie `dvag:schaden:posteingang`.
 */
export const DVAG_ORDNER_PRAEFIX = "dvag:";
export const dvagOrdnerKey = (ordner: DvagOrdner, bereich: DvagBereich = "kontakt"): string =>
    `${DVAG_ORDNER_PRAEFIX}${bereich === "schaden" ? "schaden:" : ""}${ordner}`;
export function dvagOrdnerAusKey(
    key: string | null | undefined,
): { bereich: DvagBereich; ordner: DvagOrdner } | null {
    for (const bereich of ["kontakt", "schaden"] as const) {
        for (const ordner of ["posteingang", "archiv"] as const) {
            if (key === dvagOrdnerKey(ordner, bereich)) return { bereich, ordner };
        }
    }
    return null;
}

/** „2026-10-09" → „09.10.2026". */
export function dvagDatum(iso: string): string {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso ?? "");
    return m ? `${m[3]}.${m[2]}.${m[1]}` : iso ?? "";
}

/** Kopfzeile einer Listenzeile: „Vertragspost · Schaden". */
export function dvagArtZeile(z: Pick<DvagPostZeile, "art" | "kategorie">): string {
    return [z.art, z.kategorie].map((t) => t.trim()).filter(Boolean).join(" · ") || "Kontakthistorie";
}

/** Überschrift der Datumsgruppe in der Liste. */
export function dvagDatumsGruppe(iso: string, heute = new Date()): string {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso ?? "");
    if (!m) return "Ohne Datum";
    const tag = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    const ref = new Date(heute.getFullYear(), heute.getMonth(), heute.getDate());
    const diff = Math.round((ref.getTime() - tag.getTime()) / 86_400_000);
    if (diff === 0) return "Heute";
    if (diff === 1) return "Gestern";
    const wochentag = ["Sonntag", "Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag"][tag.getDay()];
    return `${wochentag}, ${dvagDatum(iso)}`;
}
